import {map} from 'rxjs/operators';
import {Inject, Injectable} from '@angular/core';
import {ConfProxy} from '../../../common/http/services/conf.proxy';
import {HttpObservable} from '../../../common/http/services/http.wrapper';
import {LookupRequest} from '../request.lookup';
import {ILookupMeta} from '../interfaces/Imeta.lookup';
import {ILookupTable} from '../interfaces/Itable.lookup';
import {LookupDelete} from "../interfaces/lookup.delete";
import {LookupUpdate} from "../interfaces/lookup.update";
import * as _ from "lodash";
import {IBaseFilter, IComplexFilter, instanceOfIComplexFilter} from "..";
import {SortOrder} from "../interfaces/Isort.lookup";
import {AuthenticatedMode, AuthenticatedModeContainer} from "../../../app/app.module";
import {DOCUMENT} from "@angular/common";

/**
 * ***************************************************************
 *  The proxy used for server communication.
 *
 *  Written by: Dana Moshe
 * ***************************************************************
 */
@Injectable({ providedIn: 'root' })
  export class LookupsProxy {

	//Max int in C# 32 bit.
	MAX_INT:number = 2147483647;

	public dateParser = new RegExp(".*");

	constructor(private confHttp:ConfProxy,@Inject(AuthenticatedMode) private authenticationMode:AuthenticatedModeContainer) {
	}

	public getLookupByMultiColumnSearch(lookup_name:string, lookup_table:string, filter_columns:string[],  sort_column:string , sort_order: SortOrder,
										filter_string: string,  min_row:number, max_row:number,extra_filter:IBaseFilter):HttpObservable<ILookupTable<any>>{
		return this.confHttp.get("Lookups", "GetLookupByMultiColumnSearch",
			[{pName: "lookup_name", pVal: lookup_name},
				{pName: "lookup_table", pVal: lookup_table},
				{pName: "filter_columns", pVal: filter_columns},
				{pName: "sort_column", pVal: sort_column?sort_column:''},
				{pName: "sort_order", pVal: sort_order?sort_order:SortOrder.asc},
				{pName: "filter_string", pVal: filter_string},
				{pName: "min_row", pVal: min_row},
				{pName: "extra_filter", pVal: extra_filter},
				{pName: "max_row", pVal: max_row}]
		).pipe(map(raw => this.confHttp.parseDates(raw, this.dateParser)));
	}

	public getLookups(lookupRequests:LookupRequest[]):HttpObservable<ILookupTable<any>[][]> {
		// Remove default values to shorten URL in splited mode
		let requestsCopy = lookupRequests.map((request, idx) => {
			let newRequest = _.cloneDeep(request);

			if (newRequest.includeClobAndBlob === false) {
				delete newRequest.includeClobAndBlob;
			}

			if (newRequest.nMinRow === 0) {
				delete newRequest.nMinRow;
			}

			if (newRequest.nMaxRow === this.MAX_INT) {
				delete newRequest.nMaxRow;
			}

			this.setSamePropertyForAllFilters(newRequest,"value","valueForFilters");
			this.setSamePropertyForAllFilters(newRequest,"operator","operatorForFilters");
			this.setSamePropertyForAllFilters(newRequest,"isCaseInvariant","isCaseInvariantForFilters");

			return newRequest;
		});
		let dataObservable;
		if (this.authenticationMode.mode){
			dataObservable =  this.confHttp.getSplitted("Lookups", "GetLookups",
				{pName: "r", pVal: requestsCopy}, true);
		}else{
			dataObservable= this.confHttp.getSplitted("LookupsAnon", "GetLookups",
				{pName: "r", pVal: requestsCopy}, true,true);
		}

		return dataObservable.pipe(map(raw => this.confHttp.parseDates(raw, this.dateParser)));
	}

	public setSamePropertyForAllFilters(newRequest:LookupRequest, origFieldName:keyof IBaseFilter, generalFieldName:keyof IComplexFilter) {
		if (newRequest.filter && instanceOfIComplexFilter(newRequest.filter)) {
			let allfilterValues = [];
			let accumulateFilterValues = ((currFilter: IBaseFilter) => {
				allfilterValues.push(currFilter[origFieldName]);
			});
			newRequest.visitBaseFilters(accumulateFilterValues, newRequest.filter);
			if (allfilterValues && (allfilterValues.find(currValue=>currValue!==allfilterValues[0])===undefined)){
				// we have a single value used for all filters
				newRequest.filter[generalFieldName]=allfilterValues[0];
				let removeFilterValue=((currFilter:IBaseFilter)=>{
					delete currFilter[origFieldName] ;
				});
				newRequest.visitBaseFilters(removeFilterValue,newRequest.filter);
			}
		}
	}

	public listLookups(filter:string):HttpObservable<ILookupMeta[]> {
		return this.confHttp.get("Lookups", "GetListLookups", {pName: "filter", pVal: filter});
	}

	public getComplexLookups(lookupsNamesAndBlob:{lookupName:string;includeClob:boolean}[],filterActive:boolean):HttpObservable<any[]> {
		if (this.authenticationMode.mode){
			return this.confHttp.get("Lookups", "GetComplexLookups",
				[{pName: "lookupsNames", pVal: lookupsNamesAndBlob.map(x=>x.lookupName+':'+x.includeClob)},
					{pName: "filterActive", pVal: filterActive}])
				.pipe(map(raw => this.confHttp.parseDates(raw, this.dateParser)));
		}else{
			return this.confHttp.getAnon("LookupsAnon", "GetComplexLookupsAnon",
				[{pName: "lookupsNames", pVal: lookupsNamesAndBlob.map(x=>x.lookupName+':'+x.includeClob)},
					{pName: "filterActive", pVal: filterActive}])
				.pipe(map(raw => this.confHttp.parseDates(raw, this.dateParser)));
		}
	}

	public insert(lookupName:string, data:any) : HttpObservable<void> {
		return this.confHttp.post("Lookups", "Insert", data,
			{pName: "lookupName", pVal: lookupName});
	}

	public update(lookupName:string, data:any) : HttpObservable<void> {
		return this.confHttp.put("Lookups", "Update", data,
			{pName: "lookupName", pVal: lookupName});
	}

	public delete(request:LookupDelete) : HttpObservable<void> {
		return this.confHttp.delete("Lookups", "Delete",
			{pName: "request", pVal: request});
	}
}
